import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import ModalBase from "../../components/ModalBase";
import { FormGrid } from "../../components/FormCard";
import { DetailRow, InputRow, SelectRow, SwitchRow } from "../../components/Rows";
import LoadingSpinner from "../../components/LoadingSpinner";

const ACCESS_METHOD_VALUES = [
  "OPEN",
  "TOKEN",
  "LICENSE_PLATE",
  "ACCESS_CODE",
  "INTERCOM",
  "PARKING_TICKET",
];

const getAccessMethodOptions = (t) =>
  ACCESS_METHOD_VALUES.map((value) => ({
    value,
    label: t(`booking.termsModal.accessMethod.${value}`),
  }));

const coerceIntOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const asBoolOrNull = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  return value === true;
};

const asNumberString = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }

  return String(value);
};

const formatYesNo = (value, t) => {
  if (value === true) {
    return t("common.yes");
  }

  if (value === false) {
    return t("common.no");
  }

  return "-";
};

const formatMinutes = (value, t) => {
  if (value === "" || value === null || value === undefined) {
    return "-";
  }

  return t("booking.termsModal.minutes", { value });
};

const formatAccessMethods = (methods, t) => {
  if (!Array.isArray(methods) || methods.length === 0) {
    return "-";
  }

  return methods
    .map((method) => t(`booking.termsModal.accessMethod.${method}`, { defaultValue: String(method).replace(/_/g, " ") }))
    .join(", ");
};

const getInitialForm = (terms, bookingLocationPublicId) => ({
  bookingLocationPublicId: bookingLocationPublicId || terms?.bookingLocationPublicId || "",
  rfidAuthRequired: asBoolOrNull(terms?.rfidAuthRequired),
  tokenGroupsSupported: asBoolOrNull(terms?.tokenGroupsSupported),
  remoteAuthSupported: asBoolOrNull(terms?.remoteAuthSupported),
  supportedAccessMethods: Array.isArray(terms?.supportedAccessMethods) ? terms.supportedAccessMethods : [],
  changeUntilMinutes: asNumberString(terms?.changeUntilMinutes ?? 0, "0"),
  cancelUntilMinutes: asNumberString(terms?.cancelUntilMinutes ?? 0, "0"),
  changeNotAllowed: asBoolOrNull(terms?.changeNotAllowed),
  earlyStartAllowed: asBoolOrNull(terms?.earlyStartAllowed),
  earlyStartTimeMinutes: asNumberString(terms?.earlyStartTimeMinutes, ""),
  noshowTimeoutMinutes: asNumberString(terms?.noshowTimeoutMinutes, ""),
  noshowFee: asBoolOrNull(terms?.noshowFee),
  lateStopAllowed: asBoolOrNull(terms?.lateStopAllowed),
  lateStopTimeMinutes: asNumberString(terms?.lateStopTimeMinutes, ""),
  minBookingDurationMinutes: asNumberString(terms?.minBookingDurationMinutes, ""),
  maxBookingDurationMinutes: asNumberString(terms?.maxBookingDurationMinutes, ""),
  bookingTermsUrl: terms?.bookingTermsUrl ?? "",
});

const BookingTermsReadOnlyView = ({ form }) => {
  const { t } = useTranslation();
  return (
  <FormGrid>
    <DetailRow
      label={t("booking.termsModal.changeUntil")}
      tooltip={t("booking.termsModal.tooltip.changeUntilMinutes")}
      value={formatMinutes(form.changeUntilMinutes, t)}
    />
    <DetailRow
      label={t("booking.termsModal.cancelUntil")}
      tooltip={t("booking.termsModal.tooltip.cancelUntilMinutes")}
      value={formatMinutes(form.cancelUntilMinutes, t)}
    />
    <DetailRow
      label={t("booking.termsModal.rfidAuth")}
      tooltip={t("booking.termsModal.tooltip.rfidAuthRequired")}
      value={formatYesNo(form.rfidAuthRequired, t)}
    />
    <DetailRow
      label={t("booking.termsModal.tokenGroups")}
      tooltip={t("booking.termsModal.tooltip.tokenGroupsSupported")}
      value={formatYesNo(form.tokenGroupsSupported, t)}
    />
    <DetailRow
      label={t("booking.termsModal.remoteAuth")}
      tooltip={t("booking.termsModal.tooltip.remoteAuthSupported")}
      value={formatYesNo(form.remoteAuthSupported, t)}
    />
    <DetailRow
      label={t("booking.termsModal.changeNotAllowed")}
      tooltip={t("booking.termsModal.tooltip.changeNotAllowed")}
      value={formatYesNo(form.changeNotAllowed, t)}
    />
    <DetailRow
      label={t("booking.termsModal.earlyStartAllowed")}
      tooltip={t("booking.termsModal.tooltip.earlyStartAllowed")}
      value={formatYesNo(form.earlyStartAllowed, t)}
    />
    <DetailRow
      label={t("booking.termsModal.earlyStartTime")}
      tooltip={t("booking.termsModal.tooltip.earlyStartTimeMinutes")}
      value={formatMinutes(form.earlyStartTimeMinutes, t)}
    />
    <DetailRow
      label={t("booking.termsModal.noshowTimeout")}
      tooltip={t("booking.termsModal.tooltip.noshowTimeoutMinutes")}
      value={formatMinutes(form.noshowTimeoutMinutes, t)}
    />
    <DetailRow
      label={t("booking.termsModal.noshowFee")}
      tooltip={t("booking.termsModal.tooltip.noshowFee")}
      value={formatYesNo(form.noshowFee, t)}
    />
    <DetailRow
      label={t("booking.termsModal.lateStopAllowed")}
      tooltip={t("booking.termsModal.tooltip.lateStopAllowed")}
      value={formatYesNo(form.lateStopAllowed, t)}
    />
    <DetailRow
      label={t("booking.termsModal.lateStopTime")}
      tooltip={t("booking.termsModal.tooltip.lateStopTimeMinutes")}
      value={formatMinutes(form.lateStopTimeMinutes, t)}
    />
    <DetailRow
      label={t("booking.termsModal.minDuration")}
      tooltip={t("booking.termsModal.tooltip.minBookingDurationMinutes")}
      value={formatMinutes(form.minBookingDurationMinutes, t)}
    />
    <DetailRow
      label={t("booking.termsModal.maxDuration")}
      tooltip={t("booking.termsModal.tooltip.maxBookingDurationMinutes")}
      value={formatMinutes(form.maxBookingDurationMinutes, t)}
    />
    <div className="col-span-1 md:col-span-2">
      <DetailRow
        label={t("booking.termsModal.accessMethods")}
        tooltip={t("booking.termsModal.tooltip.supportedAccessMethods")}
        value={formatAccessMethods(form.supportedAccessMethods, t)}
      />
    </div>
    <div className="col-span-1 md:col-span-2">
      <DetailRow
        label={t("booking.termsModal.termsUrl")}
        tooltip={t("booking.termsModal.tooltip.bookingTermsUrl")}
        value={form.bookingTermsUrl || "-"}
      />
    </div>
  </FormGrid>
  );
};

const BookingTermsEditView = ({ form, setForm, fieldsDisabled }) => {
  const { t } = useTranslation();
  return (
  <FormGrid>
    <InputRow
      label={t("booking.termsModal.changeUntil")}
      tooltip={t("booking.termsModal.tooltip.changeUntilMinutes")}
      value={form.changeUntilMinutes}
      onChange={(value) => setForm((current) => ({ ...current, changeUntilMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <InputRow
      label={t("booking.termsModal.cancelUntil")}
      tooltip={t("booking.termsModal.tooltip.cancelUntilMinutes")}
      value={form.cancelUntilMinutes}
      onChange={(value) => setForm((current) => ({ ...current, cancelUntilMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.rfidAuth")}
      tooltip={t("booking.termsModal.tooltip.rfidAuthRequired")}
      checked={form.rfidAuthRequired === true}
      onChange={(value) => setForm((c) => ({ ...c, rfidAuthRequired: value }))}
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.tokenGroups")}
      tooltip={t("booking.termsModal.tooltip.tokenGroupsSupported")}
      checked={form.tokenGroupsSupported === true}
      onChange={(value) => setForm((c) => ({ ...c, tokenGroupsSupported: value }))}
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.remoteAuth")}
      tooltip={t("booking.termsModal.tooltip.remoteAuthSupported")}
      checked={form.remoteAuthSupported === true}
      onChange={(value) => setForm((c) => ({ ...c, remoteAuthSupported: value }))}
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.changeNotAllowed")}
      tooltip={t("booking.termsModal.tooltip.changeNotAllowed")}
      checked={form.changeNotAllowed === true}
      onChange={(value) => setForm((c) => ({ ...c, changeNotAllowed: value }))}
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.earlyStartAllowed")}
      tooltip={t("booking.termsModal.tooltip.earlyStartAllowed")}
      checked={form.earlyStartAllowed === true}
      onChange={(value) => setForm((c) => ({ ...c, earlyStartAllowed: value }))}
      disabled={fieldsDisabled}
    />

    <InputRow
      label={t("booking.termsModal.earlyStartTime")}
      tooltip={t("booking.termsModal.tooltip.earlyStartTimeMinutes")}
      value={form.earlyStartTimeMinutes}
      onChange={(value) => setForm((current) => ({ ...current, earlyStartTimeMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <InputRow
      label={t("booking.termsModal.noshowTimeout")}
      tooltip={t("booking.termsModal.tooltip.noshowTimeoutMinutes")}
      value={form.noshowTimeoutMinutes}
      onChange={(value) => setForm((current) => ({ ...current, noshowTimeoutMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.noshowFee")}
      tooltip={t("booking.termsModal.tooltip.noshowFee")}
      checked={form.noshowFee === true}
      onChange={(value) => setForm((c) => ({ ...c, noshowFee: value }))}
      disabled={fieldsDisabled}
    />

    <SwitchRow
      label={t("booking.termsModal.lateStopAllowed")}
      tooltip={t("booking.termsModal.tooltip.lateStopAllowed")}
      checked={form.lateStopAllowed === true}
      onChange={(value) => setForm((c) => ({ ...c, lateStopAllowed: value }))}
      disabled={fieldsDisabled}
    />

    <InputRow
      label={t("booking.termsModal.lateStopTime")}
      tooltip={t("booking.termsModal.tooltip.lateStopTimeMinutes")}
      value={form.lateStopTimeMinutes}
      onChange={(value) => setForm((current) => ({ ...current, lateStopTimeMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <InputRow
      label={t("booking.termsModal.minDuration")}
      tooltip={t("booking.termsModal.tooltip.minBookingDurationMinutes")}
      value={form.minBookingDurationMinutes}
      onChange={(value) => setForm((current) => ({ ...current, minBookingDurationMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <InputRow
      label={t("booking.termsModal.maxDuration")}
      tooltip={t("booking.termsModal.tooltip.maxBookingDurationMinutes")}
      value={form.maxBookingDurationMinutes}
      onChange={(value) => setForm((current) => ({ ...current, maxBookingDurationMinutes: value }))}
      isNumber
      disabled={fieldsDisabled}
    />

    <div className="col-span-1 md:col-span-2">
      <SelectRow
        label={t("booking.termsModal.accessMethods")}
        tooltip={t("booking.termsModal.tooltip.supportedAccessMethods")}
        value={form.supportedAccessMethods}
        options={getAccessMethodOptions(t)}
        onChange={(value) => setForm((current) => ({ ...current, supportedAccessMethods: value }))}
        isMulti
        disabled={fieldsDisabled}
      />
    </div>

    <div className="col-span-1 md:col-span-2">
      <InputRow
        label={t("booking.termsModal.termsUrl")}
        tooltip={t("booking.termsModal.tooltip.bookingTermsUrl")}
        value={form.bookingTermsUrl}
        onChange={(value) => setForm((current) => ({ ...current, bookingTermsUrl: value }))}
        disabled={fieldsDisabled}
      />
    </div>
  </FormGrid>
  );
};

const BookingTermsModal = ({
  isOpen,
  onClose,
  connectorLabel,
  bookingLocationPublicId,
  terms,
  loading = false,
  saving = false,
  onSave,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => getInitialForm(null, bookingLocationPublicId));
  const fieldsDisabled = readOnly || saving;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setForm(getInitialForm(terms, bookingLocationPublicId));
  }, [bookingLocationPublicId, isOpen, terms]);

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose?.();
  };

  const handleSubmit = async () => {
    if (readOnly || saving || loading || !onSave) {
      return;
    }

    const payload = {
      isGlobal: false,
      bookingLocationPublicId: form.bookingLocationPublicId || bookingLocationPublicId || null,
      changeUntilMinutes: coerceIntOrNull(form.changeUntilMinutes) ?? 0,
      cancelUntilMinutes: coerceIntOrNull(form.cancelUntilMinutes) ?? 0,
      rfidAuthRequired: form.rfidAuthRequired,
      tokenGroupsSupported: form.tokenGroupsSupported,
      remoteAuthSupported: form.remoteAuthSupported,
      supportedAccessMethods: Array.isArray(form.supportedAccessMethods) ? form.supportedAccessMethods : [],
      changeNotAllowed: form.changeNotAllowed,
      earlyStartAllowed: form.earlyStartAllowed,
      earlyStartTimeMinutes: coerceIntOrNull(form.earlyStartTimeMinutes),
      noshowTimeoutMinutes: coerceIntOrNull(form.noshowTimeoutMinutes),
      noshowFee: form.noshowFee,
      lateStopAllowed: form.lateStopAllowed,
      lateStopTimeMinutes: coerceIntOrNull(form.lateStopTimeMinutes),
      minBookingDurationMinutes: coerceIntOrNull(form.minBookingDurationMinutes),
      maxBookingDurationMinutes: coerceIntOrNull(form.maxBookingDurationMinutes),
      bookingTermsUrl: form.bookingTermsUrl || null,
    };

    await onSave(payload);
  };

  return (
    <ModalBase isOpen={isOpen} onClose={handleClose} size="lg" className="px-4 py-4 sm:px-8 sm:py-8">
      <div className="flex h-[calc(100dvh-4rem)] min-h-0 flex-col gap-4 overflow-hidden sm:h-[85vh]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-primary-700 sm:text-2xl">{t("booking.termsModal.title")}</h2>
            <p className="mt-1 text-sm text-mute-500">
              {connectorLabel
                ? t("booking.termsModal.connector", { name: connectorLabel })
                : readOnly
                  ? t("booking.termsModal.viewHelp")
                  : t("booking.termsModal.editHelp")}
            </p>
          </div>
          {(loading || saving) && (
            <FontAwesomeIcon icon={faSpinnerThird} spin className="mt-1 text-lg text-primary-500" />
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {loading ? (
            <div className="rounded-2xl border border-mute-200 bg-white px-6 py-12 text-center text-sm">
              <LoadingSpinner text={t("booking.termsModal.loading")} />
            </div>
          ) : readOnly ? (
            <BookingTermsReadOnlyView form={form} />
          ) : (
            <BookingTermsEditView
              form={form}
              setForm={setForm}
              fieldsDisabled={fieldsDisabled}
            />
          )}
        </div>

        <div className="mt-auto flex w-full flex-row flex-nowrap items-center justify-between gap-2 overflow-x-auto border-t border-mute-200 pt-4 sm:overflow-visible">
          <button
            type="button"
            className="btn btn-outline-primary shrink-0 whitespace-nowrap justify-center"
            onClick={handleClose}
            disabled={saving}
          >
            {t("common.close")}
          </button>

          {!readOnly && (
            <button
              type="button"
              className="btn btn-primary shrink-0 whitespace-nowrap justify-center"
              onClick={handleSubmit}
              disabled={saving || loading || !bookingLocationPublicId}
              title={!bookingLocationPublicId ? t("booking.termsModal.missingId") : t("booking.termsModal.saveTerms")}
            >
              {saving ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : t("common.save")}
            </button>
          )}
        </div>
      </div>
    </ModalBase>
  );
};

export default BookingTermsModal;
